/**
 * Definition docs content (frontend) — the plain-English layer the /governance
 * /definitions catalogue reads over the registry spine. The registry still owns
 * every governed fact (formula, format, direction, lineage view, drill
 * contract); this module only adds documentation *around* those facts so the
 * page reads like a reference manual, not a debug table:
 *   - overview : one clean sentence — what the metric IS, non-technical.
 *   - source   : one sentence — where the data originates and how it reaches the
 *                served number (the lineage narrative in words).
 * Keyed by registry metric id. Anything missing falls back to the registry
 * formula alone (the card degrades honestly), so a new metric is never a crash.
 */

import type { Cadence, RegistryMetric } from '../types/metrics'

/** The three upstream systems the lineage entity keys (sage/fm/ra) point at,
 *  rendered as the names a reader recognises rather than the two-letter code. */
export const SOURCE_SYSTEMS: Record<string, { name: string; detail: string }> = {
  sage: { name: 'Sage Intacct', detail: 'group finance ledger' },
  fm: { name: 'Factory Master', detail: 'Kirkstall ERP' },
  ra: { name: 'Red Ant', detail: 'Oracle-site ERP' },
}

export interface DefinitionDoc {
  overview: string
  source: string
}

/** what-it-is + where-it-comes-from, per metric id. Drawn from the registry
 *  formula / note / lineage — restated in plain words, never invented. */
export const definitionDocs: Record<string, DefinitionDoc> = {
  revenue: {
    overview:
      'The money billed to customers for goods sold in the month — the top line of the P&L. A month that has closed shows what was billed. A month still running, and every month ahead, shows the forecast instead, so the row always carries a complete year and never sets part of a month against a whole month of plan.',
    source:
      "Closed months are read from the finance ledger: the 'Revenue' account group in Sage Intacct, taken as a monthly balance. Open months are read from the blended-revenue view — firm order call-offs plus forecast orders in Factory Master for Kirkstall, and Sage actual-to-date for Oracle, which has no forward forecast in Red Ant. The Group figure is the sum of the two sites.",
  },
  revenue_firm: {
    overview:
      'Revenue already secured by signed customer orders — what is contractually on the books to ship, ignoring anything merely forecast.',
    source:
      "Summed from open firm purchase-order call-off lines in Factory Master (Kirkstall's order book); past months are blank by design.",
  },
  gross_profit: {
    overview:
      "What remains of revenue after the direct cost of making the goods — revenue minus cost of sales.",
    source:
      "Derived in the master view from two Sage Intacct account groups: 'Revenue' and 'Cost of Sales'.",
  },
  gpm: {
    overview:
      'Gross profit as a percentage of revenue — how many pence in every sales pound survive direct costs.',
    source:
      'Computed per site and month in the master view from Sage Intacct actuals; the target is taken straight from the planning spreadsheet, not derived.',
  },
  ebitda: {
    overview:
      'Trading profit before interest, tax, depreciation and amortisation — a clean read on operating performance.',
    source: "The 'Trading EBITDA' account group balance in Sage Intacct finance.",
  },
  absence_rate: {
    overview: 'The share of scheduled working time lost to sickness absence, as a percentage.',
    source:
      "Absence days and headcount from each site's HR data — Factory Master at Kirkstall and Red Ant at Oracle — over available working days, in the per-site aggregate views.",
  },
  recordables: {
    overview: 'The count of recordable safety incidents (accidents) in the period.',
    source:
      'FM incidents logged as ACCIDENT in the Kirkstall health-and-safety log, plus Oracle incidents from the Red Ant SharePoint safety data.',
  },
  obs_hazards: {
    overview:
      'The count of safety observations and hazards colleagues raised — a leading indicator of safety engagement.',
    source:
      "Raised-item counts from both sites' safety systems (Factory Master and Red Ant SharePoint), captured weekly.",
  },
  near_miss: {
    overview:
      "The count of near-miss events reported — incidents that could have caused harm but didn't.",
    source:
      'FM health-and-safety NEARMISS entries and safety-issue rows at Kirkstall, plus Red Ant SharePoint safety data at Oracle.',
  },
  first_aid: {
    overview: 'The count of first-aid treatments recorded in the period.',
    source:
      "First-aid entries from each site's safety system — Factory Master (Kirkstall) and Red Ant (Oracle).",
  },
  nc_batch: {
    overview:
      'The share of production scrapped as non-conforming — quality loss as a percentage of what was made.',
    source:
      'Scrap and batch quantities from each site’s shopfloor system; the two sites genuinely measure the denominator differently (FM scrap ÷ (scrap + goods-in) vs RA scrap ÷ total completed).',
  },
  otif: {
    overview:
      'The share of customer call-offs delivered On Time In Full — the whole quantity, delivered inside the agreed window.',
    source:
      'Delivery and call-off data from Factory Master (per month/call-off) and Red Ant (per delivery); the FM rule was rewritten on 10 Jul, so the v1/v2 variants are pinned.',
  },
  utilisation: {
    overview:
      'How hard the CNC machines were running — operating hours against the hours theoretically available.',
    source:
      "Machine run + set time from Factory Master and Red Ant over a 24h-day resource base. Note: Utilisation's improvement direction in the catalogue is counterintuitive — carried as-is and flagged for sign-off.",
  },
  past_due: {
    overview: 'The value of customer orders that have slipped past their promised delivery date.',
    source:
      "Overdue order-line values at the latest month-end snapshot from each site's order book (Factory Master and Red Ant).",
  },
  cogs: {
    overview:
      'The direct cost of the goods sold in the month — the materials and direct labour that go into the product.',
    source:
      "The 'Cost of Sales' account group in Sage Intacct, validated equal to the old purchases-plus-direct-staff derivation.",
  },
  opex: {
    overview:
      'The running costs of the business below gross profit — overheads not tied directly to a product.',
    source:
      "The 'OPEX' account group in Sage Intacct. Currently KNOWN-WRONG (KAL-385): the group's composition understates the true figure until Liam corrects it in Sage.",
  },
  depreciation: {
    overview:
      'The periodic write-down of fixed assets — the accounting cost of assets wearing out.',
    source: "The 'Depreciation & Amortisation – Summary P&L' account group in Sage Intacct.",
  },
  mfg_inventory: {
    overview:
      'How many days of manufacturing stock the business is holding — a working-capital efficiency measure.',
    source:
      'Sage Intacct stock balance over average daily purchases, computed in the working-capital view.',
  },
  debtor_days: {
    overview: 'How long, on average, customers take to pay — days of sales tied up in unpaid invoices.',
    source:
      'Trade-debtor balances from Sage Intacct (VAT stripped, ÷1.2) over average daily revenue, in the working-capital view.',
  },
  creditor_days: {
    overview: 'How long, on average, the business takes to pay suppliers — days of purchases owed.',
    source:
      'Trade-creditor balances from Sage Intacct (VAT stripped, ÷1.2) over average daily purchases, in the working-capital view.',
  },
  oee: {
    overview:
      'Overall Equipment Effectiveness — a combined availability × performance × quality score for the equipment.',
    source:
      'Actuals source not yet confirmed: the cutover OEE rows are commented out and no aggregate view is captured, so enabling this is an escalation item.',
  },
}

/** The cadence's lineage views, mapped to reader-facing system names.
 *  Empty when no source view is captured for that cadence (rendered honestly). */
export function sourceViews(
  metric: RegistryMetric,
  cadence: Cadence
): { system: string; detail: string; view: string }[] {
  const map = cadence === 'weekly' ? metric.lineage.weekly_agg : metric.lineage.monthly_agg
  if (!map) return []
  return Object.entries(map).map(([entity, view]) => ({
    system: SOURCE_SYSTEMS[entity]?.name ?? entity,
    detail: SOURCE_SYSTEMS[entity]?.detail ?? '',
    view,
  }))
}

/** Free-text catalogue filter — matches title, served metric, pillar or id.
 *  Empty query matches everything (the print pack always renders in full). */
export function metricMatches(metric: RegistryMetric, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [metric.title, metric.served_metric, metric.pillar, metric.id]
    .join(' ')
    .toLowerCase()
    .includes(q)
}
